import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
  FileText,
  Plus,
  Edit2,
  Trash2,
  Search,
  Copy,
  Eye,
  X,
  Check,
  Send,
  Users,
  ClipboardList,
  Layout,
  Type,
  AlignLeft,
  Hash,
  ChevronDown,
  CheckSquare,
  Circle,
  Sliders,
  Calendar,
  Clock,
  Mail,
  Phone,
  Upload,
  PenTool,
  Heading,
  GripVertical,
  ChevronUp,
  Loader2,
  FileCheck,
  CheckCircle,
  ArrowLeft
} from 'lucide-react';

const FIELD_ICONS = {
  text: Type,
  textarea: AlignLeft,
  number: Hash,
  select: ChevronDown,
  multiselect: CheckSquare,
  radio: Circle,
  checkbox: CheckSquare,
  scale: Sliders,
  date: Calendar,
  time: Clock,
  email: Mail,
  phone: Phone,
  file: Upload,
  signature: PenTool,
  heading: Heading,
  paragraph: FileText
};

const CATEGORY_CONFIG = {
  intake: { name: 'Client Intake', color: 'text-blue-400', bg: 'bg-blue-500/10' },
  checkin: { name: 'Check-in', color: 'text-green-400', bg: 'bg-green-500/10' },
  assessment: { name: 'Assessment', color: 'text-purple-400', bg: 'bg-purple-500/10' },
  feedback: { name: 'Feedback', color: 'text-yellow-400', bg: 'bg-yellow-500/10' },
  waiver: { name: 'Waiver/Consent', color: 'text-red-400', bg: 'bg-red-500/10' },
  goal_setting: { name: 'Goal Setting', color: 'text-teal-400', bg: 'bg-teal-500/10' },
  custom: { name: 'Custom', color: 'text-gray-400', bg: 'bg-gray-500/10' }
};

export default function FormsPage() {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('forms');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [viewingSubmissions, setViewingSubmissions] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [fillingForm, setFillingForm] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'custom',
    fields: [],
    is_active: true,
    is_required_for_onboarding: false,
    auto_assign_to_new_clients: false,
    allow_multiple_submissions: false,
    notification_on_submit: true
  });
  
  const [expandedField, setExpandedField] = useState(null);
  
  const isTrainer = user?.role === 'trainer' || user?.role === 'admin';

  const fetchForms = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const res = await axios.get(`${API}/forms?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setForms(res.data.forms || []);
    } catch (error) {
      console.error('Error fetching forms:', error);
    } finally {
      setLoading(false);
    }
  }, [token, searchTerm, categoryFilter]);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/forms/templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    fetchForms();
    if (isTrainer) fetchTemplates();
  }, [fetchForms, isTrainer]);

  const handleCreateForm = () => {
    setFormData({
      name: '',
      description: '',
      category: 'custom',
      fields: [],
      is_active: true,
      is_required_for_onboarding: false,
      auto_assign_to_new_clients: false,
      allow_multiple_submissions: false,
      notification_on_submit: true
    });
    setEditingForm(null);
    setShowCreateModal(true);
  };

  const handleEditForm = (form) => {
    setFormData({
      name: form.name,
      description: form.description || '',
      category: form.category,
      fields: form.fields || [],
      is_active: form.is_active,
      is_required_for_onboarding: form.is_required_for_onboarding,
      auto_assign_to_new_clients: form.auto_assign_to_new_clients,
      allow_multiple_submissions: form.allow_multiple_submissions,
      notification_on_submit: form.notification_on_submit
    });
    setEditingForm(form);
    setShowCreateModal(true);
  };

  const handleSubmitForm = async () => {
    if (!formData.name.trim()) {
      toast.error('Form name is required');
      return;
    }
    
    if (formData.fields.length === 0) {
      toast.error('Add at least one field');
      return;
    }
    
    try {
      if (editingForm) {
        await axios.put(`${API}/forms/${editingForm.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Form updated');
      } else {
        await axios.post(`${API}/forms`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Form created');
      }
      setShowCreateModal(false);
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save form');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Delete this form and all submissions?')) return;
    
    try {
      await axios.delete(`${API}/forms/${formId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form deleted');
      fetchForms();
    } catch (error) {
      toast.error('Failed to delete form');
    }
  };

  const handleCreateFromTemplate = async (templateId) => {
    try {
      await axios.post(`${API}/forms/from-template/${templateId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form created from template');
      setShowTemplatesModal(false);
      fetchForms();
    } catch (error) {
      toast.error('Failed to create from template');
    }
  };

  const handleViewSubmissions = async (form) => {
    try {
      const res = await axios.get(`${API}/forms/${form.id}/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmissions(res.data.submissions || []);
      setViewingSubmissions(form);
    } catch (error) {
      toast.error('Failed to load submissions');
    }
  };

  const addField = (type) => {
    const newField = {
      id: `field_${Date.now()}`,
      type,
      label: '',
      placeholder: '',
      required: false,
      options: type === 'select' || type === 'multiselect' || type === 'radio' || type === 'checkbox' ? ['Option 1'] : undefined,
      min_value: type === 'scale' ? 1 : undefined,
      max_value: type === 'scale' ? 10 : undefined,
      help_text: ''
    };
    setFormData(prev => ({ ...prev, fields: [...prev.fields, newField] }));
    setExpandedField(newField.id);
  };

  const updateField = (fieldId, updates) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.map(f => f.id === fieldId ? { ...f, ...updates } : f)
    }));
  };

  const removeField = (fieldId) => {
    setFormData(prev => ({
      ...prev,
      fields: prev.fields.filter(f => f.id !== fieldId)
    }));
  };

  const moveField = (fromIndex, toIndex) => {
    setFormData(prev => {
      const newFields = [...prev.fields];
      const [removed] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, removed);
      return { ...prev, fields: newFields };
    });
  };

  // Form filling handlers
  const handleFillForm = async (form) => {
    try {
      const res = await axios.get(`${API}/forms/${form.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFillingForm(res.data);
      setFormResponses({});
    } catch (error) {
      toast.error('Failed to load form');
    }
  };

  const handleSubmitFormResponse = async () => {
    if (!fillingForm) return;
    
    // Validate required fields
    const missingFields = fillingForm.fields
      .filter(f => f.required && f.type !== 'heading' && f.type !== 'paragraph')
      .filter(f => {
        const value = formResponses[f.id];
        if (value === undefined || value === null || value === '') return true;
        if (Array.isArray(value) && value.length === 0) return true;
        return false;
      });
    
    if (missingFields.length > 0) {
      toast.error(`Please fill in: ${missingFields[0].label}`);
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/forms/${fillingForm.id}/submit`, {
        responses: formResponses
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Form submitted successfully!');
      setFillingForm(null);
      setFormResponses({});
      fetchForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormField = (field) => {
    const value = formResponses[field.id];
    
    switch (field.type) {
      case 'heading':
        return <h3 className="text-lg font-semibold text-white mt-4 mb-2 border-b border-border pb-2">{field.label}</h3>;
      
      case 'paragraph':
        return <p className="text-gray-400 text-sm mb-4 whitespace-pre-wrap">{field.label}</p>;
      
      case 'text':
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          />
        );
      
      case 'email':
        return (
          <input
            type="email"
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder || 'email@example.com'}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          />
        );
      
      case 'phone':
        return (
          <input
            type="tel"
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder || '(555) 555-5555'}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          />
        );
      
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
            rows={3}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none"
          />
        );
      
      case 'number':
        return (
          <input
            type="number"
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          />
        );
      
      case 'select':
        return (
          <select
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          >
            <option value="">Select an option...</option>
            {(field.options || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </select>
        );
      
      case 'radio':
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-secondary/50">
                <input
                  type="radio"
                  name={field.id}
                  checked={value === opt}
                  onChange={() => setFormResponses(prev => ({ ...prev, [field.id]: opt }))}
                  className="mt-1"
                />
                <span className="text-white text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      
      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div className="space-y-2">
            {(field.options || []).map((opt, i) => (
              <label key={i} className="flex items-start gap-3 cursor-pointer p-2 rounded hover:bg-secondary/50">
                <input
                  type="checkbox"
                  checked={checkboxValues.includes(opt)}
                  onChange={(e) => {
                    const newValues = e.target.checked 
                      ? [...checkboxValues, opt]
                      : checkboxValues.filter(v => v !== opt);
                    setFormResponses(prev => ({ ...prev, [field.id]: newValues }));
                  }}
                  className="mt-1"
                />
                <span className="text-white text-sm">{opt}</span>
              </label>
            ))}
          </div>
        );
      
      case 'scale':
        const minVal = field.min_value || 0;
        const maxVal = field.max_value || 10;
        return (
          <div>
            <div className="flex justify-between mb-2">
              {Array.from({ length: maxVal - minVal + 1 }, (_, i) => minVal + i).map(num => (
                <button
                  key={num}
                  onClick={() => setFormResponses(prev => ({ ...prev, [field.id]: num }))}
                  className={`w-10 h-10 rounded-lg font-medium transition-colors ${
                    value === num 
                      ? 'bg-primary text-black' 
                      : 'bg-secondary text-gray-400 hover:bg-primary/20'
                  }`}
                >
                  {num}
                </button>
              ))}
            </div>
            {field.help_text && <p className="text-gray-500 text-xs mt-1">{field.help_text}</p>}
          </div>
        );
      
      case 'signature':
        return (
          <div className="border border-border rounded-lg p-4 bg-secondary/50">
            <input
              type="text"
              value={value || ''}
              onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
              placeholder="Type your full name as signature"
              className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white italic"
            />
            <p className="text-gray-500 text-xs mt-2">By typing your name above, you agree this serves as your electronic signature.</p>
          </div>
        );
      
      default:
        return (
          <input
            type="text"
            value={value || ''}
            onChange={(e) => setFormResponses(prev => ({ ...prev, [field.id]: e.target.value }))}
            placeholder={field.placeholder}
            className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
          />
        );
    }
  };

  const FormCard = ({ form }) => {
    const category = CATEGORY_CONFIG[form.category] || CATEGORY_CONFIG.custom;
    const hasSubmitted = form.user_submission !== null && form.user_submission !== undefined;
    const isSystemForm = form.is_system_form || form.trainer_id === 'system';
    
    return (
      <div className="bg-card border border-border rounded-lg p-4 hover:border-primary/50 transition-all">
        <div className="flex items-start justify-between mb-3">
          <div className={`px-2 py-1 rounded text-xs font-medium ${category.bg} ${category.color}`}>
            {category.name}
          </div>
          <div className="flex items-center gap-2">
            {form.is_active ? (
              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">Active</span>
            ) : (
              <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 rounded text-xs">Inactive</span>
            )}
          </div>
        </div>
        
        <h3 className="text-white font-semibold mb-1">{form.name}</h3>
        <p className="text-gray-400 text-sm mb-3 line-clamp-2">{form.description || 'No description'}</p>
        
        <div className="flex items-center gap-3 mb-3 text-sm text-gray-500">
          <span>{form.fields?.length || 0} fields</span>
          {form.submission_count !== undefined && (
            <span>{form.submission_count} submissions</span>
          )}
        </div>
        
        <div className="flex gap-2">
          {isTrainer ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleViewSubmissions(form)}
                className="flex-1"
              >
                <ClipboardList className="w-4 h-4 mr-1" />
                Submissions
              </Button>
              {!isSystemForm && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditForm(form)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteForm(form.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </>
          ) : hasSubmitted ? (
            <div className="flex items-center gap-2 flex-1">
              <span className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm flex items-center gap-2 flex-1 justify-center">
                <CheckCircle className="w-4 h-4" />
                Submitted {new Date(form.user_submission.submitted_at).toLocaleDateString()}
              </span>
              {form.allow_multiple_submissions && (
                <Button size="sm" variant="outline" onClick={() => handleFillForm(form)}>
                  Submit Again
                </Button>
              )}
            </div>
          ) : (
            <Button size="sm" className="flex-1" onClick={() => handleFillForm(form)}>
              <FileCheck className="w-4 h-4 mr-1" />
              Fill Out
            </Button>
          )}
        </div>
      </div>
    );
  };

  const FieldEditor = ({ field, index }) => {
    const FieldIcon = FIELD_ICONS[field.type] || FileText;
    const isExpanded = expandedField === field.id;
    
    return (
      <div className="border border-border rounded-lg bg-secondary/30">
        <div 
          className="p-3 flex items-center gap-3 cursor-pointer"
          onClick={() => setExpandedField(isExpanded ? null : field.id)}
        >
          <GripVertical className="w-4 h-4 text-gray-500 cursor-move" />
          <FieldIcon className="w-4 h-4 text-primary" />
          <span className="text-white flex-1">{field.label || `${field.type} field`}</span>
          {field.required && <span className="text-red-400 text-xs">Required</span>}
          {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
        
        {isExpanded && (
          <div className="p-3 border-t border-border space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Label *</label>
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => updateField(field.id, { label: e.target.value })}
                  className="w-full px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                  placeholder="Field label"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={field.placeholder || ''}
                  onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                  className="w-full px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                  placeholder="Placeholder text"
                />
              </div>
            </div>
            
            {(field.type === 'select' || field.type === 'multiselect' || field.type === 'radio' || field.type === 'checkbox') && (
              <div>
                <label className="block text-xs text-gray-400 mb-1">Options</label>
                {(field.options || []).map((opt, i) => (
                  <div key={i} className="flex gap-2 mb-1">
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => {
                        const newOptions = [...field.options];
                        newOptions[i] = e.target.value;
                        updateField(field.id, { options: newOptions });
                      }}
                      className="flex-1 px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                    />
                    <button
                      onClick={() => {
                        const newOptions = field.options.filter((_, idx) => idx !== i);
                        updateField(field.id, { options: newOptions });
                      }}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateField(field.id, { options: [...(field.options || []), ''] })}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              </div>
            )}
            
            {field.type === 'scale' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Min Value</label>
                  <input
                    type="number"
                    value={field.min_value || 1}
                    onChange={(e) => updateField(field.id, { min_value: parseInt(e.target.value) })}
                    className="w-full px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Max Value</label>
                  <input
                    type="number"
                    value={field.max_value || 10}
                    onChange={(e) => updateField(field.id, { max_value: parseInt(e.target.value) })}
                    className="w-full px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                  />
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-xs text-gray-400 mb-1">Help Text</label>
              <input
                type="text"
                value={field.help_text || ''}
                onChange={(e) => updateField(field.id, { help_text: e.target.value })}
                className="w-full px-3 py-1.5 bg-card border border-border rounded text-white text-sm"
                placeholder="Additional instructions"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={field.required}
                  onChange={(e) => updateField(field.id, { required: e.target.checked })}
                  className="rounded border-border"
                />
                Required field
              </label>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeField(field.id)}
                className="text-red-400 hover:text-red-300"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Remove
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="forms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Forms & Questionnaires</h1>
          <p className="text-gray-400">Create custom forms for intake, check-ins, and assessments</p>
        </div>
        
        {isTrainer && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTemplatesModal(true)}>
              <Layout className="w-4 h-4 mr-2" />
              Templates
            </Button>
            <Button onClick={handleCreateForm} data-testid="create-form-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Form
            </Button>
          </div>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="Search forms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary"
          />
        </div>
        
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2 bg-card border border-border rounded-lg text-white focus:outline-none focus:border-primary"
        >
          <option value="">All Categories</option>
          {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
            <option key={key} value={key}>{name}</option>
          ))}
        </select>
      </div>

      {/* Forms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {forms.map(form => (
          <FormCard key={form.id} form={form} />
        ))}
        
        {forms.length === 0 && (
          <div className="col-span-full text-center py-12 text-gray-400">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{isTrainer ? 'No forms created yet' : 'No forms assigned to you'}</p>
            {isTrainer && (
              <Button onClick={() => setShowTemplatesModal(true)} className="mt-4" variant="outline">
                Start from Template
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                {editingForm ? 'Edit Form' : 'Create Form'}
              </h2>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 grid grid-cols-3 gap-4">
              {/* Left - Form Settings */}
              <div className="col-span-1 space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Form Name *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                    placeholder="e.g., Weekly Check-in"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white resize-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-white"
                  >
                    {Object.entries(CATEGORY_CONFIG).map(([key, { name }]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                      className="rounded border-border"
                    />
                    Form is active
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.auto_assign_to_new_clients}
                      onChange={(e) => setFormData(prev => ({ ...prev, auto_assign_to_new_clients: e.target.checked }))}
                      className="rounded border-border"
                    />
                    Auto-assign to new clients
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allow_multiple_submissions}
                      onChange={(e) => setFormData(prev => ({ ...prev, allow_multiple_submissions: e.target.checked }))}
                      className="rounded border-border"
                    />
                    Allow multiple submissions
                  </label>
                  
                  <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.notification_on_submit}
                      onChange={(e) => setFormData(prev => ({ ...prev, notification_on_submit: e.target.checked }))}
                      className="rounded border-border"
                    />
                    Notify on submission
                  </label>
                </div>
                
                <div className="border-t border-border pt-4">
                  <div className="text-sm text-gray-400 mb-2">Add Field</div>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(FIELD_ICONS).slice(0, 10).map(([type, Icon]) => (
                      <button
                        key={type}
                        onClick={() => addField(type)}
                        className="flex items-center gap-2 p-2 bg-secondary hover:bg-secondary/80 rounded-lg text-sm text-gray-300 transition-colors"
                      >
                        <Icon className="w-4 h-4" />
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Right - Fields Builder */}
              <div className="col-span-2 space-y-3">
                <div className="text-sm text-gray-400 mb-2">Form Fields ({formData.fields.length})</div>
                
                {formData.fields.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 border border-dashed border-border rounded-lg">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Add fields from the left panel</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {formData.fields.map((field, index) => (
                      <FieldEditor key={field.id} field={field} index={index} />
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmitForm} className="flex-1" data-testid="save-form-btn">
                {editingForm ? 'Update Form' : 'Create Form'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Templates Modal */}
      {showTemplatesModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Form Templates</h2>
              <button onClick={() => setShowTemplatesModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4 space-y-3">
              {templates.map(template => {
                const category = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.custom;
                return (
                  <div 
                    key={template.id}
                    className="border border-border rounded-lg p-4 hover:border-primary/50 transition-all"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className={`inline-block px-2 py-0.5 rounded text-xs mb-2 ${category.bg} ${category.color}`}>
                          {category.name}
                        </div>
                        <h3 className="text-white font-semibold">{template.name}</h3>
                        <p className="text-gray-400 text-sm mt-1">{template.description}</p>
                        <p className="text-gray-500 text-xs mt-2">{template.fields.length} fields</p>
                      </div>
                      <Button onClick={() => handleCreateFromTemplate(template.id)}>
                        Use Template
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Submissions Modal */}
      {viewingSubmissions && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{viewingSubmissions.name}</h2>
                <p className="text-gray-400 text-sm">{submissions.length} submissions</p>
              </div>
              <button onClick={() => setViewingSubmissions(null)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-4">
              {submissions.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No submissions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {submissions.map(sub => (
                    <div key={sub.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-white font-medium">{sub.user_name || 'Anonymous'}</div>
                          <div className="text-gray-500 text-sm">{sub.user_email}</div>
                        </div>
                        <div className="text-gray-500 text-sm">
                          {new Date(sub.submitted_at).toLocaleDateString()}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        {Object.entries(sub.responses || {}).map(([fieldId, value]) => {
                          const field = viewingSubmissions.fields?.find(f => f.id === fieldId);
                          return (
                            <div key={fieldId} className="flex justify-between text-sm">
                              <span className="text-gray-400">{field?.label || fieldId}:</span>
                              <span className="text-white">
                                {Array.isArray(value) ? value.join(', ') : String(value)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Form Filling Modal */}
      {fillingForm && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-card border border-border rounded-xl w-full max-w-2xl my-8 mx-4">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center gap-4 z-10">
              <button 
                onClick={() => { setFillingForm(null); setFormResponses({}); }} 
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">{fillingForm.name}</h2>
                <p className="text-gray-400 text-sm">{fillingForm.description}</p>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              {fillingForm.fields?.map((field, index) => {
                // Check conditional visibility
                if (field.conditional) {
                  const conditionValue = formResponses[field.conditional.field];
                  if (conditionValue !== field.conditional.value) {
                    return null;
                  }
                }
                
                return (
                  <div key={field.id || index}>
                    {field.type !== 'heading' && field.type !== 'paragraph' && (
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        {field.label}
                        {field.required && <span className="text-red-400 ml-1">*</span>}
                      </label>
                    )}
                    {renderFormField(field)}
                  </div>
                );
              })}
            </div>
            
            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => { setFillingForm(null); setFormResponses({}); }} 
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitFormResponse} 
                className="flex-1"
                disabled={submitting}
                data-testid="submit-form-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Form
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
